import { useActiveRole, useUser } from '@/contexts/UserSession';
import { UserRole } from '@/gql/enums';

const useAssessmentResultAbilities = () => {
    const user = useUser(true);
    const activeRole = useActiveRole();
    const role = activeRole?.role || null;
    const company = user.__typename === 'TenantUser' ? user.company : null;

    // Viewing: allowed for authenticated users scoped by company (or via SuperAdmin wrapper at index level)
    const canViewAssessmentResults = () => {
        return !!company?.id || user.__typename === 'SuperAdminUser';
    };

    // Manual create/update/delete: only for trainees on their own results
    const canAddManualAssessmentResult = () => {
        return user.__typename === 'TenantUser' && role === UserRole.Trainee;
    };

    const canEditOwnAssessmentResults = () => {
        return user.__typename === 'TenantUser';
    };

    const canDeleteOwnAssessmentResults = () => {
        return user.__typename === 'TenantUser';
    };

    // Run-linked create/update/delete: CompanyAdmin/Trainer within their own company
    const canManageRunLinkedAssessmentResults = () => {
        return !!company && [UserRole.CompanyAdmin, UserRole.Trainer].includes(role!);
    };

    const canManageAssessmentResultsForCompany = (companyId: string) => {
        return !!company?.id && company.id === companyId && [UserRole.CompanyAdmin, UserRole.Trainer].includes(role!);
    };

    const displayAssessmentResultMenuItem = () => {
        return canViewAssessmentResults();
    };

    return {
        canViewAssessmentResults,
        canAddManualAssessmentResult,
        canEditOwnAssessmentResults,
        canDeleteOwnAssessmentResults,
        canManageRunLinkedAssessmentResults,
        canManageAssessmentResultsForCompany,
        displayAssessmentResultMenuItem,
    };
};

export type AssessmentResultAbilities = ReturnType<typeof useAssessmentResultAbilities>;
export default useAssessmentResultAbilities;
